// Styles
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import './styles/toast.css';

import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import { AuthStateMonitor } from '@components/Auth';
import { MobileAppBanner } from '@components/MobileAppBanner';
import { UnloggedSignupModal } from '@components/Modals/UnloggedSignupModal';
import { StationSettings } from '@components/StationSettings';
import { useStationMode, useStationSettingsCapture } from '@hooks/station';
import { useFavicon } from '@hooks/useFavicon';
import { useInitializeUserData } from '@hooks/useInitializeUserData';
import Musicplayer from '@pages/Musicplayer';
import { brazeService } from '@services/braze';
import { analytics } from '@utils/analytics';
import AvatarSession from './features/AvatarSession';
import { useStationWebSocket } from './hooks/station';
import { useCommonState } from './hooks/storeHooks/useCommonState';
import { useMusicState } from './hooks/storeHooks/useMusicState';
import { DESKTOP_BREAKPOINT } from './hooks/useIsDesktop';
import { useNativeBridgeAuth } from './hooks/useNativeBridgeAuth';
import Router, { shouldHideSidebarAndHeader } from './router/Router';
import {
  muteNativeCompanion,
  updateNativeCompanionVolume,
} from '@utils/station';
import {
  setNavigateFunction,
  startBusinessPlanGeneration,
  startImageGeneration,
  startMusicGeneration,
  startPresentationGeneration,
  startWebpageGeneration,
} from './utils/station/generationBridge';
import {
  duckAudio,
  playCreation,
  playTrackOrAlbum,
  setPlaybackFunctions,
} from './utils/station/playbackBridge';
import {
  regenerateBusinessPlan,
  regenerateCardImage,
  regenerateImage,
  regenerateLyrics,
  regeneratePresentation,
  regenerateWebpage,
} from './utils/station/regenerationBridge';
import {
  performSearch,
  setNavigateFunction as setSearchNavigateFunction,
} from './utils/station/searchBridge';
import {
  goToNext,
  shareGeneration,
  switchToNextVariant,
} from './utils/station/uiActionsBridge';
import { wsStartSpacesUseCase } from './utils/station/bridge';

// Page map for analytics tracking
export const PAGE_MAP: Record<string, string> = {
  '/': 'Home',
  '/favorites': 'Favorites',
  '/playlists': 'My Playlists',
  '/my-creations': 'My Creations',
  '/musicforyou': 'Music',
  '/serenity-wellness': 'Serenity',
  '/audiobooks': 'Audiobooks',
  '/companions': 'Companions',
  '/pricing': 'Pricing',
  '/account': 'My Account / Overview',
  '/account/billing': 'My Account / Billing',
  '/generation': 'Song Generation',
  '/oldplaylists': 'Playlists Migration',
  '/offers': 'Campaign Landing',
};

// Helper function to get page name from pathname
const getPageName = (pathname: string): string => {
  // Check for exact match first
  if (PAGE_MAP[pathname]) {
    return PAGE_MAP[pathname];
  }

  // Check for partial matches (e.g., /generation/:slug/:taskId)
  if (pathname.startsWith('/generation/')) {
    return PAGE_MAP['/generation'];
  }

  if (pathname.startsWith('/offers/')) {
    return PAGE_MAP['/offers'];
  }

  // Check for account routes
  if (pathname.startsWith('/account/billing')) {
    return PAGE_MAP['/account/billing'];
  }
  if (pathname.startsWith('/account')) {
    return PAGE_MAP['/account'];
  }

  // Return pathname if no match found
  return pathname;
};

function App() {
  useNativeBridgeAuth();
  useFavicon();
  useInitializeUserData();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    setIsDirectPlayerAccess,
    setPlaylistOpen,
    isUnloggedGenerationModalOpen,
    setIsUnloggedGenerationModalOpen,
    setActivePopup,
  } = useCommonState();
  const {
    setActivePlaylist,
    addRecentTrack,
    preferredLocale,
    volume,
    setVolume,
  } = useMusicState();

  /*const isAppNapsterAi =
    typeof window !== 'undefined' &&
    window.location.hostname === 'app.napster.ai';*/

  // Check if current URL is a direct player URL
  useEffect(() => {
    const isPlayerUrl =
      location.pathname.match(/^\/player\/([^\/]+)\/([^\/]+)$/) ||
      location.pathname.match(/^\/creations\/track\/([^\/]+)\/([^\/]+)$/i);
    setIsDirectPlayerAccess(!!isPlayerUrl);
  }, [location.pathname, setIsDirectPlayerAccess]);

  // Handle window resize to update playlistOpen state based on screen size
  useEffect(() => {
    const handleResize = () => {
      const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;
      setPlaylistOpen(isDesktop);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setPlaylistOpen]);

  // Check for mode=ces or mode=station URL parameter to hide sidebar only
  const isStationMode = useStationMode();

  // Remembers every station parameter the kiosk arrives with, since generation
  // redirects to a URL that carries none of them
  useStationSettingsCapture();

  // Initialize WebSocket connection for Station mode (conditional on communication=websockets)
  const {
    isEnabled: wsEnabled,
    isConnected: wsConnected,
    error: wsError,
  } = useStationWebSocket();

  // Log WebSocket status in development
  useEffect(() => {
    if (import.meta.env.DEV && wsEnabled) {
      console.log(
        '[Station WS] Status:',
        wsConnected ? 'Connected' : 'Disconnected',
      );
      if (wsError) {
        console.error('[Station WS] Error:', wsError);
      }
    }
  }, [wsEnabled, wsConnected, wsError]);

  // Add/remove Station mode class to body for CSS styling
  useEffect(() => {
    if (isStationMode) {
      document.body.classList.add('station-mode');
    } else {
      document.body.classList.remove('station-mode');
    }
    return () => {
      document.body.classList.remove('station-mode');
    };
  }, [isStationMode]);

  const hideFooter = useMemo(
    () =>
      isStationMode ||
      location.pathname.startsWith('/account') ||
      shouldHideSidebarAndHeader(location.pathname),
    [location.pathname, isStationMode],
  );

  const hideSidebarAndHeader = useMemo(
    () =>
      shouldHideSidebarAndHeader(location.pathname) ||
      (isStationMode &&
        (location.pathname.startsWith('/generation/') ||
          location.pathname.startsWith('/station/spaces'))),
    [location.pathname, isStationMode],
  );

  // Hide sidebar when hideSidebarAndHeader is true
  // Compute individual flags for Router
  const { hideSidebar, hideHeader, hideFooterComputed } = useMemo(
    () => ({
      hideSidebar: hideSidebarAndHeader,
      hideHeader: hideSidebarAndHeader,
      hideFooterComputed: hideFooter || hideSidebarAndHeader,
    }),
    [hideFooter, hideSidebarAndHeader],
  );

  useEffect(() => {
    const detectiPhoneChrome = () => {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isChrome =
        /CriOS/.test(navigator.userAgent) || /Chrome/.test(navigator.userAgent);
      const isSafari =
        /Safari/.test(navigator.userAgent) &&
        !/Chrome/.test(navigator.userAgent);

      if (isIOS && isChrome && !isSafari) {
        document.body.classList.add('iphone-chrome');
      } else {
        document.body.classList.remove('iphone-chrome');
      }
    };

    detectiPhoneChrome();
  }, []);

  // Track previous path to detect navigation
  const previousPathRef = useRef<string>(location.pathname);
  const previousPageNameRef = useRef<string>(getPageName(location.pathname));

  // Close modal when navigating to public pages (only if modal is open)
  useEffect(() => {
    const publicPages = ['/', '/music', '/serenity', '/wisdom'];
    const previousPath = previousPathRef.current;

    // Close modal only if we actually navigated (path changed) to a public page
    if (
      isUnloggedGenerationModalOpen &&
      location.pathname !== previousPath &&
      publicPages.includes(location.pathname)
    ) {
      setIsUnloggedGenerationModalOpen(false);
    }

    previousPathRef.current = location.pathname;
  }, [
    location.pathname,
    isUnloggedGenerationModalOpen,
    setIsUnloggedGenerationModalOpen,
  ]);

  useEffect(() => {
    setNavigateFunction(navigate);
    setSearchNavigateFunction(navigate);
    setPlaybackFunctions(
      setActivePlaylist,
      setActivePopup,
      addRecentTrack,
      () => preferredLocale,
      setVolume,
      () => volume,
    );

    if (typeof window !== 'undefined' && isStationMode) {
      window.startMusicGeneration = startMusicGeneration;
      window.startImageGeneration = startImageGeneration;
      window.startPresentationGeneration = startPresentationGeneration;
      window.startBusinessPlanGeneration = startBusinessPlanGeneration;
      window.startWebpageGeneration = startWebpageGeneration;
      window.loadSpacesUseCase = wsStartSpacesUseCase;
      window.performSearch = performSearch;
      window.playTrackOrAlbum = playTrackOrAlbum;
      window.playCreation = playCreation;
      window.regenerateLyrics = regenerateLyrics;
      window.regenerateCardImage = regenerateCardImage;
      window.regenerateImage = regenerateImage;
      window.regeneratePresentation = regeneratePresentation;
      window.regenerateBusinessPlan = regenerateBusinessPlan;
      window.regenerateWebpage = regenerateWebpage;
      window.duckAudio = duckAudio;
      window.goToNext = goToNext;
      window.shareGeneration = shareGeneration;
      window.switchToNextVariant = switchToNextVariant;
      window.updateNativeCompanionVolume = updateNativeCompanionVolume;
      window.muteNativeCompanion = muteNativeCompanion;
    }

    return () => {
      if (typeof window !== 'undefined') {
        if (window.startMusicGeneration) {
          delete window.startMusicGeneration;
        }
        if (window.startImageGeneration) {
          delete window.startImageGeneration;
        }
        if (window.performSearch) {
          delete window.performSearch;
        }
        if (window.playTrackOrAlbum) {
          delete window.playTrackOrAlbum;
        }
        if (window.playCreation) {
          delete window.playCreation;
        }
        if (window.regenerateLyrics) {
          delete window.regenerateLyrics;
        }
        if (window.regenerateCardImage) {
          delete window.regenerateCardImage;
        }
        if (window.regenerateImage) {
          delete window.regenerateImage;
        }
        if (window.startPresentationGeneration) {
          delete window.startPresentationGeneration;
        }
        if (window.startBusinessPlanGeneration) {
          delete window.startBusinessPlanGeneration;
        }
        if (window.startWebpageGeneration) {
          delete window.startWebpageGeneration;
        }
        if (window.loadSpacesUseCase) {
          delete window.loadSpacesUseCase;
        }
        if (window.regeneratePresentation) {
          delete window.regeneratePresentation;
        }
        if (window.regenerateBusinessPlan) {
          delete window.regenerateBusinessPlan;
        }
        if (window.regenerateWebpage) {
          delete window.regenerateWebpage;
        }
        if (window.duckAudio) {
          delete window.duckAudio;
        }
        if (window.updateNativeCompanionVolume) {
          delete window.updateNativeCompanionVolume;
        }
        if (window.muteNativeCompanion) {
          delete window.muteNativeCompanion;
        }
      }
      setNavigateFunction(null as any);
      setSearchNavigateFunction(null as any);
    };
  }, [
    navigate,
    isStationMode,
    setActivePlaylist,
    setActivePopup,
    addRecentTrack,
    preferredLocale,
    setVolume,
    volume,
  ]);

  useEffect(() => {
    analytics.logEvent('app_opened', {
      page_url: window.location.href,
    });
  }, []);

  useEffect(() => {
    const pageName = getPageName(location.pathname);
    const previousPageName = previousPageNameRef.current;

    // Log to existing analytics
    analytics.logEvent('page_viewed', {
      page_name: pageName,
      entry_point: previousPageName,
    });

    // Log to Braze
    brazeService.logPageView(pageName, {
      entry_point: previousPageName,
    });

    previousPageNameRef.current = pageName;
  }, [location.pathname]);

  const isShareRoute = useMemo(
    () =>
      location.pathname.startsWith('/player/') ||
      location.pathname.startsWith('/creations/track/'),
    [location.pathname],
  );

  return (
    <div className="h-full relative overflow-hidden sm:flex bg-[linear-gradient(180deg,rgba(255,255,255,0.1)_0%,rgba(255,255,255,0)_100%)]">
      <AuthStateMonitor />
      <Router
        hideFooter={hideFooterComputed}
        hideHeader={hideHeader}
        hideSidebar={hideSidebar}
      />
      {/* Only render global Musicplayer when NOT on a player route to avoid duplicate audio streams */}
      <Musicplayer />
      <AvatarSession />
      {isShareRoute && !isStationMode && <MobileAppBanner />}
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
        className="mt-16"
        style={{
          fontSize: '14px',
          fontFamily: 'NapsterSans',
        }}
      />
      {/* Unlogged Generation Modal */}
      <UnloggedSignupModal
        isOpen={isUnloggedGenerationModalOpen}
        onClose={() => setIsUnloggedGenerationModalOpen(false)}
      />
      {/* Station Settings (bottom-left controls) */}
      <StationSettings />
    </div>
  );
}

export default App;
